import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type {
    BrandPromise,
    Highlight,
    LayoutData,
    SectionMap,
    Stylist,
    Testimonial
} from '@/types'

import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { CtaBand } from '@/components/site/cta-band'
import { PageHero } from '@/components/site/page-hero'
import { PromiseGrid } from '@/components/site/promise-grid'
import { SectionHeading } from '@/components/site/section-heading'
import { SmartImage } from '@/components/site/smart-image'
import { StylistCard } from '@/components/site/stylist-card'
import { TestimonialCard } from '@/components/site/testimonial-card'
import { cms } from '@/lib/cms'

import {
    getHighlights,
    getLayoutData,
    getPromises,
    getSections,
    getStylists,
    getTestimonials,
    safely
} from '@/lib/data/content'

interface AboutPageProps {
    layout: LayoutData
    sections: SectionMap
    highlights: Array<Highlight>
    promises: Array<BrandPromise>
    stylists: Array<Stylist>
    testimonials: Array<Testimonial>
}

const AboutPage: FC<AboutPageProps> = ({ layout, sections, highlights, promises, stylists, testimonials }): ReactNode => {
    const { profile } = layout

    return (
        <Layout data={layout}>
            <Seo
                title='About us'
                siteName={profile.name}
                description={profile.tagline ?? undefined}
                ogImage={profile.about_image ?? profile.hero_image}
                origin={layout.origin}
                path='/about'
            />

            <PageHero section={sections.about_page} />

            <section id='story' className='py-20 sm:py-28'>
                <div className='mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8'>
                    <div className='relative'>
                        <div className='relative aspect-[4/5] overflow-hidden rounded-[2.5rem] border border-gold-300/50 bg-muted'>
                            <SmartImage src={profile.about_image} alt={profile.about_image_alt} sizes='(min-width: 1024px) 560px, 100vw' />
                        </div>
                        <div aria-hidden='true' className='absolute -right-4 -bottom-4 -z-10 h-full w-full rounded-[2.5rem] border border-gold-300/60' />
                    </div>

                    <div>
                        {profile.about_title && (
                            <h2 data-mdlfy-cms={cms('salon_profile', profile.id, 'about_title')} className='font-heading text-4xl leading-[1.05] font-semibold text-balance text-navy-900 sm:text-5xl'>
                                {profile.about_title}
                            </h2>
                        )}
                        {profile.about_story && (
                            <p data-mdlfy-cms={cms('salon_profile', profile.id, 'about_story')} className='mt-6 text-lg leading-relaxed whitespace-pre-line text-muted-foreground'>
                                {profile.about_story}
                            </p>
                        )}

                        {highlights.length > 0 && (
                            <dl className='mt-10 grid grid-cols-2 gap-4'>
                                {highlights.slice(0, 4).map(item => (
                                    <div key={item.id} className='flex flex-col-reverse rounded-2xl border border-border/70 bg-card p-5'>
                                        <dt className='mt-1 text-sm text-muted-foreground'>
                                            <span data-mdlfy-cms={cms('highlights', item.id, 'label')}>{item.label}</span>
                                        </dt>
                                        <dd className='font-heading text-3xl font-semibold text-navy-900'>
                                            <span data-mdlfy-cms={cms('highlights', item.id, 'value')}>{item.value}</span>
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        )}
                    </div>
                </div>
            </section>

            {promises.length > 0 && (
                <section id='promise' className='bg-cream-100 py-20 sm:py-28'>
                    <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                        <SectionHeading section={sections.about_values} />
                        <div className='mt-14'>
                            <PromiseGrid promises={promises} />
                        </div>
                    </div>
                </section>
            )}

            {stylists.length > 0 && (
                <section id='team' className='py-20 sm:py-28'>
                    <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                        <SectionHeading section={sections.about_team} />
                        <div className='mt-14 grid grid-cols-2 gap-x-5 gap-y-12 lg:grid-cols-3'>
                            {stylists.map(stylist => <StylistCard key={stylist.id} stylist={stylist} showBio />)}
                        </div>
                    </div>
                </section>
            )}

            {testimonials.length > 0 && (
                <section id='testimonials' className='bg-card py-20 sm:py-24'>
                    <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                        <SectionHeading section={sections.home_testimonials} />
                        <div className='mt-12 grid gap-5 md:grid-cols-3'>
                            {testimonials.slice(0, 3).map(testimonial => <TestimonialCard key={testimonial.id} testimonial={testimonial} />)}
                        </div>
                    </div>
                </section>
            )}

            <CtaBand section={sections.home_cta} profile={profile} />
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<AboutPageProps> = async ({ req }) => {
    const [layout, sections, highlights, promises, stylists, testimonials] = await Promise.all([
        getLayoutData(req),
        safely('sections', () => getSections(['about_page', 'about_values', 'about_team', 'home_testimonials', 'home_cta']), {}),
        safely('highlights', getHighlights, []),
        safely('promises', getPromises, []),
        safely('stylists', getStylists, []),
        safely('testimonials', getTestimonials, [])
    ])

    return { props: { layout, sections, highlights, promises, stylists, testimonials } }
}

export default AboutPage
